import React from 'react';
import {
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  CircularProgress
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';

const ContextosPreguntaPopover = ({
  open,
  anchorEl,
  onClose,
  selectedPreguntaContextos = [],
  predefinedQuestions = [],
  preguntaVariables = [],
  user,
  onUpdateContextos,
  onUpdatePreguntaVariables
}) => {

  const handleRemoveContext = async (contexto) => {
    if (window.confirm(`¿Quitar contexto "${contexto.nombre}" de esta pregunta?`)) {
      // Encuentra el id de la pregunta actual
      const preguntaId = predefinedQuestions.find(p => 
        preguntaVariables.some(rel => rel.id_pregunta === p.id && rel.id_contexto === contexto.id)
      )?.id;

      if (preguntaId) {
        const res = await fetch(
          `/api/variable/?id_pregunta=${preguntaId}&id_contexto=${contexto.id}`,
          {
            method: "DELETE",
            headers: {
              "X-User-Id": String(user?.id || localStorage.getItem("user_id"))
            }
          }
        );
        
        if (!res.ok) {
          let errText = await res.text();
          let errJson = {};
          try { errJson = JSON.parse(errText); } catch {}
          let msg = errJson.detail || errText;
          if (typeof msg === 'object') msg = JSON.stringify(msg);
          alert("Error al eliminar relación pregunta-contexto: " + msg);
        } else {
          // Actualizar la lista local
          if (onUpdateContextos) {
            onUpdateContextos(prev => 
              prev.filter(ctx => ctx.id !== contexto.id)
            );
          }
          
          // Recargar datos
          if (onUpdatePreguntaVariables) {
            fetch("/api/variable/")
              .then(res => res.json())
              .then(onUpdatePreguntaVariables);
          }
        }
      }
    }
  };

  // Colores alternados para los iconos
  const iconColors = ["#6366f1", "#f472b6", "#34d399", "#fb7185", "#a78bfa", "#fbbf24"];

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      PaperProps={{
        sx: {
          minWidth: 250,
          maxHeight: 280,
          borderRadius: 2,
          boxShadow: "0 4px 16px rgba(99,102,241,0.15)",
          background: "#f8fafc",
          p: 1,
        }
      }}
      MenuListProps={{
        sx: { p: 0 }
      }}
    >
      <Box sx={{ p: 1 }}>
        <Typography variant="subtitle2" sx={{ 
          fontWeight: 700, 
          color: "#334155", 
          mb: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <LanguageIcon sx={{ fontSize: 18, color: "#6366f1" }} />
          Contextos aplicables ({selectedPreguntaContextos.length})
        </Typography>
        
        {selectedPreguntaContextos.length === 0 ? (
          <Box sx={{ 
            p: 1.5,
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.8
          }}>
            <CircularProgress size={18} sx={{ color: "#6366f1", opacity: 0.7 }} />
            <Typography variant="body2" sx={{ color: "#64748b", fontStyle: "italic", fontSize: 13 }}>
              Esta pregunta no tiene contextos asignados
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {selectedPreguntaContextos
              .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' }))
              .map((contexto, index) => {
              const colorIndex = index % iconColors.length;
              const iconColor = iconColors[colorIndex];
              
              return (
                <ListItem
                  key={contexto.id}
                  sx={{
                    borderRadius: 1,
                    m: 0.3,
                    py: 0.5,
                    transition: "all 0.2s",
                    "&:hover": {
                      background: "#e0e7ef",
                      "& .remove-button": {
                        opacity: 1,
                        transform: "translateX(0)",
                      }
                    },
                    cursor: "pointer",
                    position: "relative",
                    pr: 10,
                  }}
                  dense
                >
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <LanguageIcon sx={{ fontSize: 16, color: iconColor }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={contexto.nombre} 
                    primaryTypographyProps={{ 
                      fontSize: 12,
                      fontWeight: 500,
                      color: "#334155",
                      lineHeight: 1.2,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  />
                  
                  <Button
                    className="remove-button"
                    variant="contained"
                    size="small"
                    sx={{
                      position: "absolute",
                      right: 8,
                      minWidth: "auto",
                      fontSize: 11,
                      py: 0.2,
                      px: 1,
                      height: 24,
                      background: "#e11d48",
                      color: "#fff",
                      boxShadow: "none",
                      opacity: 0,
                      transform: "translateX(10px)",
                      transition: "opacity 0.2s, transform 0.2s",
                      "&:hover": {
                        background: "#b91c1c",
                      }
                    }}
                    onClick={async (event) => {
                      event.stopPropagation();
                      await handleRemoveContext(contexto);
                    }}
                  >
                    Quitar
                  </Button>
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>
    </Popover>
  );
};

export default ContextosPreguntaPopover;